/**
 * Servicio de almacenamiento de archivos usando Firebase Storage.
 * Los documentos sensibles (identidad, EPS, recibos) se encriptan client-side
 * con AES-256-GCM antes de subirse. Ver crypto.ts para detalles.
 */

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage, auth } from "@/lib/firebase";
import {
  generateEncryptionKey,
  encryptFile,
  type EncryptionMeta,
} from "./crypto";
import { resizeImageIfNeeded, type ResizeOptions } from "./imageResize";

/**
 * Construye el customMetadata estándar para uploads. Incluye el uid del
 * uploader para que las Storage Rules puedan restringir lectura/escritura.
 */
function buildMetadata(
  extra: Record<string, string> = {},
): Record<string, string> {
  const uid = auth.currentUser?.uid;
  return {
    ...(uid ? { uploaderUid: uid } : {}),
    ...extra,
  };
}

export interface UploadResult {
  url: string;
  path: string;
}

export interface EncryptedUploadResult extends UploadResult {
  encryption: EncryptionMeta;
  /** Resultados adicionales si se solicitaron mirror paths. */
  mirrors?: UploadResult[];
}

/** Especificación de un destino adicional al subir un archivo encriptado. */
export interface MirrorPathSpec {
  folder: string;
  filename?: string;
}

/**
 * Sube una imagen al storage (sin encriptación — para contenido no sensible).
 * Redimensiona automáticamente la imagen en el cliente antes de subir para
 * ahorrar bandwidth y storage. PDFs / formatos no-imagen se suben tal cual.
 * @param file Archivo a subir
 * @param folder Carpeta donde guardar (ej: "events", "profiles")
 * @param filename Nombre del archivo (opcional, usa timestamp si no se provee)
 * @param resize Opciones de resize (opcional). Default: 1600px lado largo, calidad 0.85.
 */
export async function uploadImage(
  file: File,
  folder: string,
  filename?: string,
  resize?: ResizeOptions,
): Promise<UploadResult> {
  const optimized = await resizeImageIfNeeded(file, resize);
  const extension = optimized.name.split(".").pop() || "jpg";
  const name =
    filename || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const path = `${folder}/${name}.${extension}`;

  const storageRef = ref(storage, path);

  // Subir archivo (posiblemente redimensionado)
  await uploadBytes(storageRef, optimized, {
    contentType: optimized.type,
    customMetadata: buildMetadata(),
  });

  // Obtener URL pública
  const url = await getDownloadURL(storageRef);

  return { url, path };
}

/**
 * Sube un archivo encriptado con AES-256-GCM al storage.
 * Genera una clave aleatoria, encripta el archivo client-side, y sube el blob cifrado.
 * La clave se retorna para almacenarla en Firestore.
 */
export async function uploadEncryptedFile(
  file: File,
  folder: string,
  filename?: string,
  mirrorPaths?: MirrorPathSpec[],
  resize?: ResizeOptions,
): Promise<EncryptedUploadResult> {
  // Optimización: si es imagen, redimensionamos ANTES de cifrar para reducir
  // tamaño del blob encriptado. PDFs y otros tipos pasan intactos.
  const optimized = await resizeImageIfNeeded(file, resize);
  console.log("[uploadEncryptedFile] START", {
    fileName: optimized.name,
    fileSize: optimized.size,
    fileType: optimized.type,
    folder,
    filename,
    mirrors: mirrorPaths?.length ?? 0,
    resized: optimized !== file,
  });

  let cryptoKey: CryptoKey;
  let exportedKey: string;
  try {
    const keyResult = await generateEncryptionKey();
    cryptoKey = keyResult.cryptoKey;
    exportedKey = keyResult.exportedKey;
    console.log("[uploadEncryptedFile] Key generated OK");
  } catch (err) {
    console.error("[uploadEncryptedFile] Key generation FAILED", err);
    throw err;
  }

  let encryptedBlob: Blob;
  try {
    encryptedBlob = await encryptFile(optimized, cryptoKey);
    console.log("[uploadEncryptedFile] Encryption OK", {
      encryptedSize: encryptedBlob.size,
    });
  } catch (err) {
    console.error("[uploadEncryptedFile] Encryption FAILED", err);
    throw err;
  }

  const name =
    filename || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const path = `${folder}/${name}.enc`;

  const storageRef = ref(storage, path);

  try {
    console.log("[uploadEncryptedFile] Uploading to Storage...", {
      path,
      blobSize: encryptedBlob.size,
    });
    await uploadBytes(storageRef, encryptedBlob, {
      contentType: "application/octet-stream",
      customMetadata: buildMetadata({
        originalType: optimized.type,
        encrypted: "AES-256-GCM",
      }),
    });
    console.log("[uploadEncryptedFile] uploadBytes OK");
  } catch (err) {
    console.error("[uploadEncryptedFile] uploadBytes FAILED", err);
    throw err;
  }

  let url: string;
  try {
    url = await getDownloadURL(storageRef);
    console.log("[uploadEncryptedFile] getDownloadURL OK");
  } catch (err) {
    console.error("[uploadEncryptedFile] getDownloadURL FAILED", err);
    throw err;
  }

  return {
    url,
    path,
    encryption: { key: exportedKey, alg: "AES-256-GCM" },
    mirrors: await uploadMirrors(
      encryptedBlob,
      optimized.type,
      mirrorPaths,
      name,
    ),
  };
}

/**
 * Sube el mismo blob encriptado a paths adicionales (mirror).
 * Permite que un archivo cifrado pueda ser leído bajo dos rule sets distintos
 * (por ejemplo `eventConsents/...` y `persons/...`).
 */
async function uploadMirrors(
  encryptedBlob: Blob,
  originalType: string,
  mirrorPaths: MirrorPathSpec[] | undefined,
  defaultName: string,
): Promise<UploadResult[] | undefined> {
  if (!mirrorPaths || mirrorPaths.length === 0) return undefined;
  const results: UploadResult[] = [];
  for (const spec of mirrorPaths) {
    const mirrorName = spec.filename || defaultName;
    const mirrorPath = `${spec.folder}/${mirrorName}.enc`;
    const mirrorRef = ref(storage, mirrorPath);
    try {
      await uploadBytes(mirrorRef, encryptedBlob, {
        contentType: "application/octet-stream",
        customMetadata: buildMetadata({
          originalType,
          encrypted: "AES-256-GCM",
        }),
      });
      const mirrorUrl = await getDownloadURL(mirrorRef);
      results.push({ url: mirrorUrl, path: mirrorPath });
      console.log("[uploadEncryptedFile] mirror OK", { mirrorPath });
    } catch (err) {
      console.error("[uploadEncryptedFile] mirror FAILED", { mirrorPath, err });
      // No bloqueamos: el path principal ya está subido.
    }
  }
  return results.length ? results : undefined;
}

/**
 * Elimina una imagen del storage
 * @param path Ruta del archivo a eliminar
 */
export async function deleteImage(path: string): Promise<void> {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}

/**
 * Elimina un archivo cualquiera del storage por path. Idempotente: silencia
 * "object-not-found" para uso en flujos de cleanup (rollback).
 */
export async function deleteFile(path: string): Promise<void> {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (err: any) {
    if (err?.code === "storage/object-not-found") return;
    throw err;
  }
}

/**
 * Sube una imagen de evento
 * @param file Archivo de imagen
 * @param eventId ID del evento (opcional para nuevos eventos)
 */
export async function uploadEventImage(
  file: File,
  eventId?: string,
): Promise<UploadResult> {
  const folder = eventId ? `events/${eventId}` : "events/temp";
  // No fijar el filename: si se reutiliza el path "cover.<ext>" cada nueva
  // subida sobrescribe el blob y los tokens de descarga emitidos previamente
  // quedan inválidos (la portada del evento anterior aparece rota).
  return uploadImage(file, folder);
}

/**
 * Sube una imagen de perfil/avatar
 * @param file Archivo de imagen
 * @param entityType Tipo de entidad ("profiles", "members", "users")
 * @param entityId ID de la entidad
 */
export async function uploadProfileImage(
  file: File,
  entityType: "profiles" | "members" | "users",
  entityId: string,
): Promise<UploadResult> {
  const folder = `${entityType}/${entityId}`;
  return uploadImage(file, folder, "avatar", { maxDimension: 800 });
}

/**
 * Sube el logo de un club
 * @param file Archivo de imagen
 * @param clubId ID del club
 */
export async function uploadClubLogo(
  file: File,
  clubId: string,
): Promise<UploadResult> {
  const folder = `clubs/${clubId}`;
  return uploadImage(file, folder, "logo", { maxDimension: 800 });
}

/**
 * Sube la foto del documento de identidad de un miembro (ENCRIPTADA)
 * @param file Archivo de imagen
 * @param personId ID de la persona
 */
export async function uploadDocumentPhoto(
  file: File,
  personId: string,
): Promise<EncryptedUploadResult> {
  const folder = `persons/${personId}`;
  return uploadEncryptedFile(file, folder, "document");
}

/**
 * Sube la foto del anverso del documento de identidad (ENCRIPTADA)
 */
export async function uploadDocumentFront(
  file: File,
  personId: string,
): Promise<EncryptedUploadResult> {
  const folder = `persons/${personId}`;
  return uploadEncryptedFile(file, folder, "document-front");
}

/**
 * Sube la foto del reverso del documento de identidad (ENCRIPTADA)
 */
export async function uploadDocumentBack(
  file: File,
  personId: string,
): Promise<EncryptedUploadResult> {
  const folder = `persons/${personId}`;
  return uploadEncryptedFile(file, folder, "document-back");
}

/**
 * Sube el soporte de EPS de un miembro (ENCRIPTADO)
 * @param file Archivo de imagen o PDF
 * @param personId ID de la persona
 */
export async function uploadEpsDocument(
  file: File,
  personId: string,
): Promise<EncryptedUploadResult> {
  const folder = `persons/${personId}`;
  return uploadEncryptedFile(file, folder, "eps");
}

/**
 * Sube un recibo de pago encriptado
 * @param file Archivo de imagen o PDF
 * @param folder Carpeta destino
 * @param filename Nombre del archivo
 */
export async function uploadEncryptedReceipt(
  file: File,
  folder: string,
  filename: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(file, folder, filename);
}

/**
 * Sube la foto tipo carnet de un miembro
 * @param file Archivo de imagen
 * @param personId ID de la persona
 */
export async function uploadMemberPhoto(
  file: File,
  personId: string,
): Promise<UploadResult> {
  const folder = `persons/${personId}`;
  return uploadImage(file, folder, "photo", { maxDimension: 800 });
}

// ============================================
// JOIN REQUEST UPLOADS
// ============================================
// Path: `joinRequests/{userId}/...`
// El personId aún no existe (se crea al aprobar la solicitud), así que
// usamos el uid del solicitante como prefijo temporal. La Cloud Function
// `approveJoinRequest` copia los URL/path/encryption al Person creado.
// Los archivos permanecen en este path (se leen por URL — el path no importa
// para el consumidor final).

function joinRequestFolder(userId: string): string {
  return `joinRequests/${userId}`;
}

/** Foto tipo carnet del solicitante (NO encriptada — pública para el carnet). */
export async function uploadJoinRequestPhoto(
  file: File,
  userId: string,
): Promise<UploadResult> {
  return uploadImage(file, joinRequestFolder(userId), "photo", {
    maxDimension: 800,
  });
}

/** Documento de identidad del solicitante — anverso (encriptado). */
export async function uploadJoinRequestDocumentFront(
  file: File,
  userId: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(file, joinRequestFolder(userId), "document-front");
}

/** Documento de identidad del solicitante — reverso (encriptado). */
export async function uploadJoinRequestDocumentBack(
  file: File,
  userId: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(file, joinRequestFolder(userId), "document-back");
}

/** Soporte de EPS del solicitante — opcional (encriptado). */
export async function uploadJoinRequestEps(
  file: File,
  userId: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(file, joinRequestFolder(userId), "eps");
}

/**
 * Valida que el archivo sea una imagen válida
 */
export function validateImageFile(file: File): {
  valid: boolean;
  error?: string;
} {
  const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  const maxSize = 5 * 1024 * 1024; // 5MB

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: "Solo se permiten imágenes JPG, PNG, WebP o GIF",
    };
  }

  if (file.size > maxSize) {
    return { valid: false, error: "La imagen no puede superar los 5MB" };
  }

  return { valid: true };
}

// ============================================
// EVENT CONSENT UPLOADS
// ============================================

/**
 * Carpeta base para los archivos de un consentimiento por evento.
 * Path: `eventConsents/{eventId}/{memberId}/...`
 */
function consentFolder(eventId: string, memberId: string): string {
  return `eventConsents/${eventId}/${memberId}`;
}

/**
 * Sube el documento de identidad del miembro como soporte del evento (encriptado).
 * @deprecated Usar uploadConsentIdDocumentFront / Back.
 */
export async function uploadConsentIdDocument(
  file: File,
  eventId: string,
  memberId: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    "id-document",
  );
}

/**
 * Sube el anverso del documento de identidad (encriptado).
 * Si se proporciona `personId`, también se mirroreja a `persons/{personId}/document-front`
 * para que pueda leerse desde el perfil del miembro bajo sus storage rules.
 */
export async function uploadConsentIdDocumentFront(
  file: File,
  eventId: string,
  memberId: string,
  personId?: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    "id-document-front",
    personId
      ? [{ folder: `persons/${personId}`, filename: "document-front" }]
      : undefined,
  );
}

/**
 * Sube el reverso del documento de identidad (encriptado).
 */
export async function uploadConsentIdDocumentBack(
  file: File,
  eventId: string,
  memberId: string,
  personId?: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    "id-document-back",
    personId
      ? [{ folder: `persons/${personId}`, filename: "document-back" }]
      : undefined,
  );
}

/**
 * Sube el certificado EPS como soporte del evento (encriptado).
 */
export async function uploadConsentEpsCertificate(
  file: File,
  eventId: string,
  memberId: string,
  personId?: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    "eps-certificate",
    personId ? [{ folder: `persons/${personId}`, filename: "eps" }] : undefined,
  );
}

/**
 * Sube una firma capturada (PNG dataURL convertido a Blob) — sin encriptar
 * porque la firma sola no es PII identificatoria y debe poder embeberse en PDFs.
 */
export async function uploadConsentSignature(
  blob: Blob,
  eventId: string,
  memberId: string,
  signerKey: string, // "member" | "parent-1" | "parent-2"
): Promise<UploadResult> {
  const path = `${consentFolder(eventId, memberId)}/signature-${signerKey}-${Date.now()}.png`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType: "image/png",
    customMetadata: buildMetadata(),
  });
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

/**
 * Sube la foto de verificación del padre (encriptada — es PII facial).
 */
export async function uploadConsentParentPhoto(
  file: File,
  eventId: string,
  memberId: string,
  parentKey: string, // "parent-1" | "parent-2"
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    `parent-photo-${parentKey}`,
  );
}

/**
 * Sube la foto del participante (encriptada — es PII facial).
 */
export async function uploadConsentParticipantPhoto(
  file: File,
  eventId: string,
  memberId: string,
): Promise<EncryptedUploadResult> {
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    "participant-photo",
  );
}

/**
 * Sube el PDF final del consentimiento (encriptado).
 */
export async function uploadConsentPdf(
  blob: Blob,
  eventId: string,
  memberId: string,
): Promise<EncryptedUploadResult> {
  // Reutilizar uploadEncryptedFile que acepta File; convertimos Blob a File
  const file = new File([blob], `consent-${Date.now()}.pdf`, {
    type: "application/pdf",
  });
  return uploadEncryptedFile(
    file,
    consentFolder(eventId, memberId),
    "consent-pdf",
  );
}

/**
 * Convierte un dataURL (base64 PNG) a Blob.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}
